import React, { useEffect, useState } from 'react';
import { Card, CardBody, Badge, Row, Col, Spinner, Alert, Table, Input, FormGroup, Label, Button, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { setBreadcrumbItems } from '../../store/actions';
import api from '../../utils/api';

const entityLabels = { journalist: 'صحفي', activist: 'ناشط', institution: 'مؤسسة' };
const actionLabels = { create: 'إنشاء', update: 'تعديل', delete: 'حذف' };
const decisionColors = { approved: 'success', rejected: 'danger', partially_approved: 'info' };
const decisionLabels = { approved: 'موافق', rejected: 'مرفوض', partially_approved: 'موافقة جزئية' };

const formatDateEN = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const ApprovalLogs = (props) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewLog, setViewLog] = useState(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState({ entity_type: '', action: '', decision: '' });

  useEffect(() => {
    props.setBreadcrumbItems('سجل الموافقات', [
      { title: 'الرئيسية', link: '/dashboard' },
      { title: 'سجل الموافقات', link: '#' },
    ]);
  }, []);

  useEffect(() => { fetchLogs(); }, [page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      if (filters.decision) params.decision = filters.decision;
      const res = await api.get('/approval-logs', { params });
      setLogs(res.data.data || []);
      setLastPage(res.data.last_page || 1);
    } catch {
      setError('فشل في جلب سجل الموافقات');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: val }));
    setPage(1);
  };

  return (
    <div>
      {error && <Alert color="danger">{error}</Alert>}

      <Card className="mb-3">
        <CardBody>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">نوع الكيان</Label>
                <Input type="select" value={filters.entity_type} onChange={e => handleFilter('entity_type', e.target.value)}>
                  <option value="">الكل</option>
                  <option value="journalist">صحفي</option>
                  <option value="activist">ناشط</option>
                  <option value="institution">مؤسسة</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">العملية</Label>
                <Input type="select" value={filters.action} onChange={e => handleFilter('action', e.target.value)}>
                  <option value="">الكل</option>
                  <option value="create">إنشاء</option>
                  <option value="update">تعديل</option>
                  <option value="delete">حذف</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label className="fw-bold">القرار</Label>
                <Input type="select" value={filters.decision} onChange={e => handleFilter('decision', e.target.value)}>
                  <option value="">الكل</option>
                  <option value="approved">موافق</option>
                  <option value="rejected">مرفوض</option>
                  <option value="partially_approved">موافقة جزئية</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /><p className="mt-2">جار التحميل...</p></div>
      ) : logs.length === 0 ? (
        <Alert color="info">لا يوجد سجلات</Alert>
      ) : (
        <Card>
          <CardBody>
            <div className="table-responsive">
              <Table className="table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>العملية</th>
                    <th>الكيان</th>
                    <th>مقدم الطلب</th>
                    <th>المراجع</th>
                    <th>القرار</th>
                    <th>الملاحظات</th>
                    <th>التاريخ</th>
                    <th>عرض</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, idx) => (
                    <tr key={log.id}>
                      <td>{(page - 1) * 15 + idx + 1}</td>
                      <td>
                        <Badge color={log.action === 'create' ? 'success' : log.action === 'update' ? 'info' : 'danger'}>
                          {actionLabels[log.action]}
                        </Badge>
                      </td>
                      <td>
                        <Badge color="primary" className="me-1">{entityLabels[log.entity_type]}</Badge>
                        <span>{log.new_data?.full_name || log.new_data?.title || log.original_data?.full_name || log.original_data?.title || '—'}</span>
                      </td>
                      <td>{log.requester?.name || '—'}</td>
                      <td>{log.reviewer?.name || '—'}</td>
                      <td>
                        <Badge color={decisionColors[log.decision]}>
                          {decisionLabels[log.decision]}
                        </Badge>
                      </td>
                      <td>{log.admin_note || '—'}</td>
                      <td style={{whiteSpace:'nowrap'}}>{formatDateEN(log.created_at)}</td>
                      <td>
                        <Button color="info" size="sm" onClick={() => setViewLog(log)}><i className="mdi mdi-eye me-1"></i>عرض</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {lastPage > 1 && (
              <div className="d-flex justify-content-center mt-3 gap-2">
                <button className="btn btn-outline-primary" disabled={page === 1} onClick={() => setPage(page - 1)}>السابق</button>
                <span className="align-self-center">صفحة {page} من {lastPage}</span>
                <button className="btn btn-outline-primary" disabled={page === lastPage} onClick={() => setPage(page + 1)}>التالي</button>
              </div>
            )}
          </CardBody>
        </Card>
      )}
      {/* Modal عرض التفاصيل */}
      {viewLog && (
        <Modal isOpen={!!viewLog} toggle={() => setViewLog(null)} size="lg" centered>
          <ModalHeader toggle={() => setViewLog(null)}>تفاصيل الطلب</ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Row>
                <Col md={6}>
                  <p><strong>العملية:</strong> <Badge color={viewLog.action === 'create' ? 'success' : viewLog.action === 'update' ? 'info' : 'danger'}>{actionLabels[viewLog.action]}</Badge></p>
                  <p><strong>الكيان:</strong> <Badge color="primary">{entityLabels[viewLog.entity_type]}</Badge></p>
                  <p><strong>القرار:</strong> <Badge color={decisionColors[viewLog.decision]}>{decisionLabels[viewLog.decision]}</Badge></p>
                </Col>
                <Col md={6}>
                  <p><strong>مقدم الطلب:</strong> {viewLog.requester?.name || '—'}</p>
                  <p><strong>المراجع:</strong> {viewLog.reviewer?.name || '—'}</p>
                  <p><strong>التاريخ:</strong> {formatDateEN(viewLog.created_at)}</p>
                </Col>
              </Row>
              {viewLog.admin_note && <p><strong>الملاحظات:</strong> {viewLog.admin_note}</p>}
            </div>

            {viewLog.action === 'update' && viewLog.field_decisions && (
              <div className="mb-3">
                <h6>قرارات الحقول:</h6>
                <Table bordered size="sm">
                  <thead className="table-light"><tr><th>الحقل</th><th>القرار</th></tr></thead>
                  <tbody>
                    {Object.entries(viewLog.field_decisions).map(([f, d]) => (
                      <tr key={f} className={d === 'approved' ? 'table-success' : 'table-danger'}>
                        <td>{f}</td>
                        <td><Badge color={d === 'approved' ? 'success' : 'danger'}>{d === 'approved' ? 'موافق' : 'مرفوض'}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {viewLog.new_data && (
              <div className="mb-3">
                <h6>{viewLog.action === 'create' ? 'البيانات المُنشأة' : viewLog.action === 'delete' ? 'البيانات المحذوفة' : 'البيانات الجديدة'}</h6>
                <Table bordered size="sm">
                  <thead className="table-light"><tr><th style={{width:'30%'}}>الحقل</th><th>القيمة</th></tr></thead>
                  <tbody>
                    {Object.entries(viewLog.new_data).filter(([k,v]) => v !== null && v !== '' && !['id','created_at','updated_at'].includes(k)).map(([k,v]) => (
                      <tr key={k}><td className="fw-bold">{k}</td><td>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</td></tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {viewLog.original_data && viewLog.action !== 'create' && (
              <div className="mb-3">
                <h6>البيانات الأصلية</h6>
                <Table bordered size="sm">
                  <thead className="table-light"><tr><th style={{width:'30%'}}>الحقل</th><th>القيمة</th></tr></thead>
                  <tbody>
                    {Object.entries(viewLog.original_data).filter(([k,v]) => v !== null && v !== '' && !['id','created_at','updated_at'].includes(k)).map(([k,v]) => (
                      <tr key={k}><td className="fw-bold">{k}</td><td>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</td></tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(ApprovalLogs);
